import { FireTVCampaignAutomationClient } from '@amzn/fire-tv-campaign-automation-service';
import safeStringify from 'fast-safe-stringify';
import { Poller } from 'src/constants';
import { ToastContextType } from 'src/contexts/ToastContext';
import {
  PollingAbortedError,
  PollingInputError,
  PollingMaxRetriesError,
  PollingSuccessConditionError,
  PollingTimeoutError,
} from 'src/errors/CustomErrors';
import { logger } from 'src/logger';

import {
  getOnTerminalErrorHandler,
  getPollerOptions,
  getPollingOperation,
  PollerMeta,
} from './PollerUtils';

/**
 * Error message constants for the Poller
 */
export const POLLER_ERROR_MESSAGES = {
  INVALID_INTERVAL: 'intervalSeconds must be positive',
  INVALID_MAX_RETRIES: 'maxRetries must be positive',
  INVALID_TIMEOUT: 'timeoutSeconds must be positive',
  ABORTED_BEFORE_START: 'Polling operation aborted before start',
  OPERATION_ABORTED: 'Polling operation aborted',
  SUCCESS_CONDITION_NOT_MET: (result: unknown) =>
    `Operation result did not meet success condition. Result: ${safeStringify(result)}`,
  MAX_RETRIES_EXCEEDED: (maxRetries: number) => `Max retries (${maxRetries}) exceeded.`,
  OPERATION_TIMEOUT: 'Operation timed out',
};

/**
 * Unified polling configuration interface that combines all polling-related settings
 */
export interface PollerOptions<T> {
  /** Interval between poll attempts in seconds */
  intervalSeconds: number;
  /** Maximum number of retry attempts */
  maxRetries: number;
  /** Optional success condition function */
  successCondition?: (result: T) => boolean;
  /** Optional error handler */
  onError?: (error: Error) => void;
  /** Optional success handler */
  onSuccess?: (result: T) => void;
  /** Optional timeout in seconds */
  timeoutSeconds?: number;
  /** Optional abort signal to cancel polling */
  abortSignal?: AbortSignal;
}

/**
 * Generic polling function that executes a given operation at specified intervals
 * @template T The type of the value returned by the operation
 * @param operation The async operation to execute
 * @param options Polling configuration options
 * @returns Promise that resolves with the operation result or rejects if max retries exceeded
 *
 * @example
 * // Example: Poll for a job status until it's complete
 * interface JobStatus {
 *   id: string;
 *   status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
 * }
 *
 * async function checkJobStatus(jobId: string): Promise<JobStatus> {
 *   const response = await fetch(`/api/jobs/${jobId}`);
 *   return response.json();
 * }
 *
 * // Usage:
 * try {
 *   const jobId = 'job123';
 *   const result = await poll(
 *     () => checkJobStatus(jobId),
 *     {
 *       intervalSeconds: 5,        // Check every 5 seconds
 *       maxRetries: 12,           // Try up to 12 times (1 minute total)
 *       timeoutSeconds: 60,       // Timeout after 1 minute
 *       successCondition: (job) => job.status === 'COMPLETED',
 *       onError: (error) => console.error('Poll attempt failed:', error),
 *       onSuccess: (job) => console.log('Job completed:', job)
 *     }
 *   );
 *   console.log('Final job status:', result);
 * } catch (error) {
 *   console.error('Job polling failed:', error);
 * }
 */
export async function poll<T>(operation: () => Promise<T>, options: PollerOptions<T>): Promise<T> {
  const {
    intervalSeconds,
    maxRetries,
    successCondition,
    onError,
    onSuccess,
    timeoutSeconds,
    abortSignal,
  } = options;

  // Validate input parameters
  if (intervalSeconds <= 0) {
    throw new PollingInputError(POLLER_ERROR_MESSAGES.INVALID_INTERVAL);
  }

  if (maxRetries <= 0) {
    throw new PollingInputError(POLLER_ERROR_MESSAGES.INVALID_MAX_RETRIES);
  }

  if (timeoutSeconds !== undefined && timeoutSeconds <= 0) {
    throw new PollingInputError(POLLER_ERROR_MESSAGES.INVALID_TIMEOUT);
  }

  // Check if already aborted
  if (abortSignal?.aborted) {
    logger.debug(POLLER_ERROR_MESSAGES.ABORTED_BEFORE_START);
    throw new PollingAbortedError();
  }

  let attempts = 0;
  const startTime = Date.now();
  const timeoutMs = timeoutSeconds ? timeoutSeconds * 1000 : undefined;
  const operationTimeoutDeadline = timeoutMs ? startTime + timeoutMs : undefined;

  // Main polling loop
  while (attempts < maxRetries) {
    try {
      const result = await executeOperation();

      // Check success condition
      if (!successCondition || successCondition(result)) {
        onSuccess?.(result);
        return result;
      }

      // If success condition not met, throw an error
      throw new PollingSuccessConditionError(result);
    } catch (error) {
      attempts++;

      // Special cases that should not retry
      if (error instanceof PollingAbortedError || error instanceof PollingTimeoutError) {
        throw error;
      }

      // If max retries reached, throw error
      if (attempts >= maxRetries) {
        const errorMessage = POLLER_ERROR_MESSAGES.MAX_RETRIES_EXCEEDED(maxRetries);
        logger.error(errorMessage, { error });
        throw new PollingMaxRetriesError(maxRetries);
      }

      // Handle error and log
      const errorObj = error instanceof Error ? error : new Error(String(error));
      onError?.(errorObj);
      logger.warn('Polling attempt failed', {
        attempt: attempts,
        maxRetries,
        error: String(error),
      });

      // Wait before next attempt
      await sleep(intervalSeconds * 1000, abortSignal);
    }
  }

  // Safety fallback - should never reach here
  throw new PollingMaxRetriesError(maxRetries);

  // Execute the operation with abort and timeout handling
  async function executeOperation(): Promise<T> {
    // Check if already aborted
    if (abortSignal?.aborted) {
      throw new PollingAbortedError();
    }

    // Check if overall timeout has been exceeded
    if (operationTimeoutDeadline && Date.now() >= operationTimeoutDeadline) {
      const errorMessage = POLLER_ERROR_MESSAGES.OPERATION_TIMEOUT;
      logger.error(errorMessage);
      throw new PollingTimeoutError();
    }

    // Create promises array for racing
    const promises: Promise<T | never>[] = [operation()];

    // Add abort promise if we have an abort signal
    if (abortSignal) {
      promises.push(
        new Promise<never>((_, reject) => {
          const abortHandler = () => {
            abortSignal.removeEventListener('abort', abortHandler);
            reject(new PollingAbortedError());
          };
          abortSignal.addEventListener('abort', abortHandler);
        }),
      );
    }

    // Add timeout promise if we have a deadline
    if (operationTimeoutDeadline) {
      const remainingTime = operationTimeoutDeadline - Date.now();
      if (remainingTime <= 0) {
        throw new PollingTimeoutError();
      }
      promises.push(createTimeoutPromise(remainingTime));
    }

    // Race all promises
    return Promise.race(promises);
  }
}

/**
 * Helper function to create a promise that resolves after a delay
 * and can be aborted with an AbortSignal
 */
async function sleep(ms: number, abortSignal?: AbortSignal): Promise<void> {
  // Check if already aborted
  if (abortSignal?.aborted) {
    throw new PollingAbortedError();
  }

  let abortHandler: (() => void) | undefined;

  const promise = new Promise<void>((resolve, reject) => {
    const timeoutId = setTimeout(resolve, ms);

    // If we have an abort signal, set up the abort handler
    if (abortSignal) {
      abortHandler = () => {
        clearTimeout(timeoutId);
        reject(new PollingAbortedError());
      };

      abortSignal.addEventListener('abort', abortHandler);
    }
  });

  // Ensure cleanup happens regardless of how the promise resolves
  return promise.finally(() => {
    if (abortSignal && abortHandler) {
      abortSignal.removeEventListener('abort', abortHandler);
    }
  });
}

/**
 * Create a promise that rejects after a specified timeout
 */
function createTimeoutPromise(timeoutMs: number): Promise<never> {
  let timeoutId: NodeJS.Timeout;
  return new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new PollingTimeoutError());
    }, timeoutMs);
  }).finally(() => clearTimeout(timeoutId));
}

//TODO: Refactor CampaignCreationSection to use this method to poll for campaign status.
// https://issues.labcollab.net/browse/EDSDAP-10051
/**
 * Execute polling operation with the given operation and options
 * @template T The type of the result returned by the operation
 * @param operation The async operation to execute
 * @param successCondition Function to determine if polling was successful
 * @param onSuccess Callback for successful polling
 * @param onError Callback for polling errors
 * @param terminalErrorHandler Handler for terminal errors that stop polling
 */
export async function executePoll<T>(
  poller: Poller,
  id: string,
  setState: (state: Partial<T>) => void,
  controlPlaneClient: FireTVCampaignAutomationClient,
  toast: ToastContextType,
  abortSignal?: AbortSignal,
  meta?: PollerMeta,
): Promise<void> {
  const operation = getPollingOperation(poller, controlPlaneClient, id);
  const pollerOptions = getPollerOptions(poller, setState, toast, abortSignal, meta);
  const terminalErrorHandler = getOnTerminalErrorHandler(poller, setState, toast, meta);
  poll(operation, pollerOptions).catch((error) => {
    logger.error('Terminal error during polling - polling has stopped', { error });
    if (terminalErrorHandler) {
      terminalErrorHandler(error);
    }
  });
}
